import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { StripeService } from 'ngx-stripe';
import { StripeElements, StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

interface PaymentIntentResponse {
    clientSecret: string;
    amount: string;
    permitNumber: string;
}

/**
 * Card payment for a guest whose permit is awaiting payment.
 *
 * The logged-in checkout can't be reused: it looks up the user's pending orders from
 * their session, and an applicant has no account. Here the permit's own token plus the
 * view grant (already proved by the email gate) authorise the intent, and the order is
 * whichever one the permit hangs off - the applicant never sees a cart.
 */
@Component({
    selector: 'app-permit-pay',
    templateUrl: './permit-pay.component.html',
    standalone: false,
})
export class PermitPayComponent implements OnInit {
    @Input() token!: string;
    /** Emitted once Stripe confirms - the parent waits for the webhook to issue the permit. */
    @Output() paid = new EventEmitter<void>();

    public amount?: string;
    public loading = true;
    public processing = false;
    public error?: string | null;
    /** Set once the card element is mounted, so the Pay button isn't offered too early. */
    public ready = false;

    private elements?: StripeElements;
    private elementsOptions: StripeElementsOptions = { locale: 'en-AU' };
    private cardOptions: StripePaymentElementOptions = {
        fields: { billingDetails: { name: 'auto', email: 'auto', phone: 'auto' } },
    };

    constructor(
        private stripeService: StripeService,
        @Inject('ApiService') private api: ApiServiceInterface,
        private grants: PermitViewGrantService,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        const grant = this.grants.read(this.token);
        if (!grant) {
            // Shouldn't happen - this component only renders behind the email gate.
            this.loading = false;
            this.error = 'Please confirm your email address again to pay.';
            return;
        }

        this.api
            .post({
                url: `/api/public/permit/${this.token}/payment-intent`,
                data: { grant },
                skipAuth: true,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<PaymentIntentResponse>) => {
                    this.amount = res.data?.amount;
                    this.loading = false;
                    this.cdRef.detectChanges();
                    this.mountCard(res.data?.clientSecret as string);
                },
                (err: ApiErrorResponse<any>) => {
                    this.loading = false;
                    this.error = err?.error?.error?.error || 'Could not start the payment. Please try again.';
                    this.cdRef.detectChanges();
                },
            );
    }

    /** Mount Stripe's payment element against the intent's client secret. */
    private mountCard(clientSecret: string): void {
        this.elementsOptions.clientSecret = clientSecret;
        this.stripeService.elements(this.elementsOptions).subscribe(elements => {
            this.elements = elements;
            if (!elements.getElement('payment')) {
                elements.create('payment', this.cardOptions);
                elements.getElement('payment')?.mount('#permit-card-element');
                elements.getElement('payment')?.on('ready', () => {
                    this.ready = true;
                    this.cdRef.detectChanges();
                });
            }
        });
    }

    public pay(): void {
        if (!this.elements || this.processing) return;
        this.processing = true;
        this.error = null;

        this.stripeService
            .confirmPayment({
                elements: this.elements,
                confirmParams: { return_url: window.location.href },
                // Stay on the page unless the card demands a redirect (3DS).
                redirect: 'if_required',
            })
            .subscribe(result => {
                this.processing = false;
                if (result.error) {
                    this.error = result.error.message || 'Your payment could not be completed.';
                    this.cdRef.detectChanges();
                    return;
                }
                // Paid. The permit is issued by the Stripe webhook, so the parent polls
                // rather than assuming it is already active.
                this.paid.emit();
            });
    }
}
